import type { TrackerCampaignListItem, TrackerStoreListItem } from '@shared/tracker-types';
import type { CampaignStatusTaskType } from '@shared/tracker-campaign-status-counts';
import {
    computeTrackerStoreProgressSummary,
    formatTrackerComplianceStatus,
    formatTrackerOperationalStatus,
} from '@shared/tracker-progress-summary';

export interface TrackerProgressExportContext {
    reportLabel: string;
    retailerName: string;
    cycleName: string;
    operatorTasksLabel: string;
    exportedAt?: Date;
}

function csvCell(value: string | number): string {
    const text = String(value);
    return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function csvLines(header: Array<string | number>, body: Array<Array<string | number>>): string {
    return [header, ...body].map((line) => line.map(csvCell).join(',')).join('\n');
}

function formatCampaignStatusSummary(store: TrackerStoreListItem): string {
    const types: CampaignStatusTaskType[] = ['POSTING', 'REMOVAL', 'REPOST'];
    const parts = types
        .map((type) => {
            const counts = store.campaignStatusCountsByType[type];
            if (counts.totalTasks === 0) {
                return null;
            }
            const label = type === 'POSTING' ? 'Posts' : type === 'REMOVAL' ? 'Removals' : 'Reposts';
            return `${label}: ${counts.submitted}/${counts.totalTasks} submitted, ${counts.approved} approved, ${counts.rejected} rejected, ${counts.awaitingReview} awaiting review`;
        })
        .filter((part): part is string => part != null);
    return parts.length > 0 ? parts.join(' | ') : 'No tasks';
}

function buildMetadataSection(context: TrackerProgressExportContext): string {
    const exportedAt = (context.exportedAt ?? new Date()).toISOString();
    return csvLines(
        ['Field', 'Value'],
        [
            ['Report', context.reportLabel],
            ['Retailer', context.retailerName],
            ['Cycle', context.cycleName],
            ['Operator Tasks', context.operatorTasksLabel],
            ['Exported At', exportedAt],
        ],
    );
}

function buildStoreSummarySection(stores: TrackerStoreListItem[]): string {
    const summary = computeTrackerStoreProgressSummary(stores);
    return csvLines(
        ['Metric', 'Count'],
        [
            ['Total Stores', summary.totalStores],
            ['Started', summary.started],
            ['Not Started', summary.notStarted],
            ['Operator Complete', summary.operatorComplete],
            ['Operator + Compliance Complete', summary.operatorAndComplianceComplete],
        ],
    );
}

export function buildTrackerStoreProgressCsv(
    stores: TrackerStoreListItem[],
    context: TrackerProgressExportContext,
): string {
    const sections = [
        buildMetadataSection(context),
        '',
        buildStoreSummarySection(stores),
        '',
        csvLines(
            [
                'Store Name',
                'Operator Status',
                'Compliance Status',
                'Campaign Status Summary',
                'Operators',
                'Pending Review',
                'Approved',
                'Rejected',
            ],
            stores.map((store) => [
                store.storeName,
                formatTrackerOperationalStatus(store.operationalStatus),
                formatTrackerComplianceStatus(store.complianceStatus),
                formatCampaignStatusSummary(store),
                (store.operatorNames ?? (store.operatorName ? [store.operatorName] : [])).join('; '),
                store.pendingReviewCount,
                store.approvedCount,
                store.rejectedCount,
            ]),
        ),
    ];
    return sections.join('\n');
}

export function buildTrackerCampaignProgressCsv(
    campaigns: TrackerCampaignListItem[],
    context: TrackerProgressExportContext,
): string {
    const completeCampaigns = campaigns.filter((campaign) => campaign.completionPercent === 100).length;
    const sections = [
        buildMetadataSection(context),
        '',
        csvLines(
            ['Metric', 'Count'],
            [
                ['Total Campaigns', campaigns.length],
                ['Campaigns 100% Store Complete', completeCampaigns],
            ],
        ),
        '',
        csvLines(
            [
                'Campaign',
                'Status',
                'Store Progress',
                'Stores Done',
                'Total Stores',
                'Started Percent',
                'Tasks Not Started',
                'Tasks In Progress',
                'Tasks Completed',
                'Tasks Rejected',
                'Retailer Ready Images',
                'Report Ready Images',
            ],
            campaigns.map((campaign) => [
                campaign.campaignName,
                campaign.status,
                `${campaign.completionPercent}%`,
                campaign.storeDoneCount,
                campaign.totalStores,
                `${campaign.startedPercent}%`,
                campaign.notStartedCount,
                campaign.inProgressCount,
                campaign.completedCount,
                campaign.rejectedCount,
                campaign.retailerReadyImagesCount,
                campaign.reportReadyImagesCount,
            ]),
        ),
    ];
    return sections.join('\n');
}

export function buildTrackerProgressExportPath(
    cycleId: string,
    retailerId: string,
    taskDefinitionIds: string | null,
): string {
    const params = new URLSearchParams();
    params.set('retailerId', retailerId);
    if (taskDefinitionIds !== null) {
        params.set('taskDefinitionIds', taskDefinitionIds);
    }
    return `/api/analytics/tracker/progress-export/${cycleId}?${params.toString()}`;
}

export function downloadTrackerProgressCsv(csv: string, filenameStem: string): void {
    const safeStem = filenameStem.toLocaleLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${safeStem || 'cockpit-export'}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
}
